import { IAuthenticationProvider, IStorageProvider } from '../@types/providers';
import { IAuthenticationResponseDto, TokenDto } from '../../global/dataTransferObjects/user';
import { IObjectUtils } from '../../utils/types';
import { isCompanyScoped, persistCompanies } from '../../global/companies';

export enum AuthenticationKeys {
  UserInfo = 'userInfo',
  AccessToken = 'accessToken',
  RefreshToken = 'refreshToken',
  LegacyAccessToken = 'legacyAccessToken',
  LegacyRefreshToken = 'legacyRefreshToken',
  Empresas = 'empresas',
  SelectedCompany = 'selectedCompany',
  GrantType = 'grantType',
}

export default (storageProvider: IStorageProvider, objectUtils: IObjectUtils): IAuthenticationProvider => {
  const getToken = (key: string): TokenDto | null => {
    const savedValue = storageProvider.getKey(key);
    if (!savedValue) {
      return null;
    }

    return objectUtils.deserializeObject<TokenDto>(savedValue);
  }

  const getAuthenticationRefreshToken = (): string | null => getToken(AuthenticationKeys.RefreshToken)?.token ?? null;

  const getAuthenticationToken = (): string | null => getToken(AuthenticationKeys.AccessToken)?.token ?? null;

  /**
   * Token do Tecpay-api, usado nas chamadas que o portal faz direto à API antiga.
   * A API antiga não aceita o token do VAULT: valida o JWT próprio e resolve o
   * usuário pelo claim de id legado.
   *
   * Ausente em sessão aberta antes da virada, onde o `accessToken` ainda é o
   * próprio token legado — por isso quem lê cai nele quando este falta.
   */
  const getLegacyAuthenticationToken = (): string | null =>
    getToken(AuthenticationKeys.LegacyAccessToken)?.token ?? null;

  const setAuthenticationInfo = (storageInfo: IAuthenticationResponseDto): void => {
    const { userInfo, token, refreshToken, businessList } = storageInfo;

    // A lista das empresas era gravada só pela tela de seleção, que apenas a PJ
    // atravessa. Gravar no login vale para qualquer tipo, e é o que dá ao seletor
    // do cabeçalho algo para oferecer.
    const companies = persistCompanies(businessList);

    // Uma empresa só não é escolha: a sessão entra direto nela. Com mais de uma a
    // decisão vai para a tela de seleção — antes o PF era jogado sempre na
    // primeira da lista, sem tela e sem seletor para trocar.
    if (isCompanyScoped(userInfo?.userType) && companies.length === 1) {
      storageProvider.setKey(
        AuthenticationKeys.SelectedCompany, objectUtils.serializeObject(companies[0]));
    }

    storageProvider.setKey(AuthenticationKeys.UserInfo, objectUtils.serializeObject(userInfo));
    storageProvider.setKey(AuthenticationKeys.AccessToken, objectUtils.serializeObject(token));
    storageProvider.setKey(AuthenticationKeys.RefreshToken, objectUtils.serializeObject(refreshToken));
  };

  const clearAuthenticationData = (): void => {
    storageProvider.removeKey(AuthenticationKeys.UserInfo);
    storageProvider.removeKey(AuthenticationKeys.AccessToken);
    storageProvider.removeKey(AuthenticationKeys.RefreshToken);
    storageProvider.removeKey(AuthenticationKeys.LegacyAccessToken);
    storageProvider.removeKey(AuthenticationKeys.LegacyRefreshToken);
    storageProvider.removeKey(AuthenticationKeys.Empresas);
    storageProvider.removeKey(AuthenticationKeys.SelectedCompany);
    storageProvider.removeKey(AuthenticationKeys.GrantType);
  };

  return ({
    getAuthenticationToken,
    getLegacyAuthenticationToken,
    setAuthenticationInfo,
    clearAuthenticationData,
    getAuthenticationRefreshToken,
  });
};
